import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../config/api';
import './AnalyticsHub.css';

export type PopularityMetricType = 'venue' | 'region' | 'artist';

export interface AnalyticsHubs {
  ga4_product_ui_url: string;
  cw_dashboard_url: string;
  athena_workgroup: string;
  athena_console_url?: string;
}

export interface PopularityItem {
  id: string;
  count: number;
  display_name?: string;
  region_name?: string;
  event_name?: string;
  updated_at?: string;
}

interface PopularityResponse {
  metric_type: string;
  window: string;
  items: PopularityItem[];
  hubs: AnalyticsHubs;
  note?: string;
}

const DEFAULT_HUBS: AnalyticsHubs = {
  ga4_product_ui_url: 'https://analytics.google.com/',
  cw_dashboard_url:
    'https://us-east-2.console.aws.amazon.com/cloudwatch/home?region=us-east-2#dashboards:name=stagespotter-analytics',
  athena_workgroup: 'stagespotter-analytics',
  athena_console_url:
    'https://us-east-2.console.aws.amazon.com/athena/home?region=us-east-2#/workgroups/details/stagespotter-analytics',
};

function yesterdayUTC(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

interface AnalyticsHubProps {
  token: string | null;
  title: string;
  metricType: PopularityMetricType;
  blurb: string;
  showRegionColumn?: boolean;
}

const AnalyticsHub: React.FC<AnalyticsHubProps> = ({
  token,
  title,
  metricType,
  blurb,
  showRegionColumn = true,
}) => {
  const [windowDay, setWindowDay] = useState(yesterdayUTC);
  const [items, setItems] = useState<PopularityItem[]>([]);
  const [hubs, setHubs] = useState<AnalyticsHubs>(DEFAULT_HUBS);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('metric_type', metricType);
      params.set('window', windowDay);
      params.set('limit', '25');
      const response = await apiRequest<PopularityResponse>(
        `/admin/analytics/popularity?${params.toString()}`,
        token
      );
      setItems(response.items || []);
      if (response.hubs) setHubs({ ...DEFAULT_HUBS, ...response.hubs });
      setNote(response.note || '');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load popularity');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, metricType, windowDay]);

  useEffect(() => {
    load();
  }, [load]);

  const entityLabel = useMemo(() => {
    switch (metricType) {
      case 'venue':
        return 'Venue';
      case 'region':
        return 'Region';
      case 'artist':
        return 'Artist';
      default:
        return 'Entity';
    }
  }, [metricType]);

  return (
    <div className="analytics-hub">
      <p className="analytics-hub-blurb">{blurb}</p>
      {note && <p className="analytics-hub-note">{note}</p>}

      <div className="analytics-hub-links">
        <a href={hubs.ga4_product_ui_url} target="_blank" rel="noopener noreferrer">
          Google Analytics (Product)
        </a>
        <a href={hubs.cw_dashboard_url} target="_blank" rel="noopener noreferrer">
          CloudWatch dashboard
        </a>
        <a
          href={hubs.athena_console_url || DEFAULT_HUBS.athena_console_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Athena ({hubs.athena_workgroup})
        </a>
      </div>

      <div className="analytics-hub-toolbar">
        <label>
          UTC day
          <input type="date" value={windowDay} onChange={(e) => setWindowDay(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <h3>
        {title} popularity — {windowDay} UTC
      </h3>
      {error && <div className="analytics-hub-error">{error}</div>}

      <div className="analytics-hub-table-wrap">
        <table className="analytics-hub-table">
          <thead>
            <tr>
              <th>{entityLabel}</th>
              {showRegionColumn && metricType !== 'region' && <th>Region</th>}
              <th style={{ width: '100px' }}>Count</th>
              <th>Sources</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={4} className="analytics-hub-empty">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="analytics-hub-empty">
                  No rollup rows for this day (empty S3 day or rollup not run yet).
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="analytics-hub-label">
                      <span className="analytics-hub-label-primary">
                        {row.display_name || row.id}
                      </span>
                      <span className="analytics-hub-label-secondary">({row.id})</span>
                    </div>
                  </td>
                  {showRegionColumn && metricType !== 'region' && (
                    <td>{row.region_name || '—'}</td>
                  )}
                  <td>{row.count}</td>
                  <td>
                    <code>{row.event_name || '—'}</code>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnalyticsHub;
